import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import getExtendedItems from '@/features/references/helpers/getExtendedItems';
import { createApi } from '@/features/utils';
import {
  CarItemType,
  ExtendedCarItemType,
  ICarOption,
} from '@/interfaces/cars.interfaces';
import { IItem, ReferenceType } from '@/interfaces/grpc.interfaces';
import { DataTypeEnum } from '@/interfaces/settings.interfaces';
import { IGrpcService } from '@/services/GrpcService';
import injectContainer from '@/services/InjectContainer';
import ServiceTypes from '@/services/ServiceTypes';

import type { CarParametersType, CarsParametersType, CarsType, CarType } from './types';

export const carsApi = createApi({
  reducerPath: 'cars',
  baseQuery: fetchBaseQuery(),
  endpoints: builder => ({
    getCars: builder.query<CarsType, CarsParametersType>({
      queryFn: async (parameters, api) => {
        const state = api.getState() as any;

        const { formatter } = state.config;

        const grpcService = injectContainer.get<IGrpcService>(ServiceTypes.Grpc);

        const { itemsClient } = grpcService;

        const { items, total } = await itemsClient.findPublished({
          ...parameters,
        });

        const carItems = items as CarItemType[];

        const extendedCarItems = await getExtendedItems<CarItemType, ExtendedCarItemType>(
          api,
          carItems,
        );

        const formattedCarItems = extendedCarItems.map(
          item => formatter?.(DataTypeEnum.Car, item) ?? item,
        ) as ExtendedCarItemType[];

        return {
          data: {
            total,
            items: formattedCarItems,
          },
        };
      },
    }),
    getCar: builder.query<CarType['item'], CarParametersType>({
      queryFn: async (parameters, api) => {
        const state = api.getState() as any;

        const { formatter } = state.config;

        const grpcService = injectContainer.get<IGrpcService>(ServiceTypes.Grpc);

        const { itemsClient } = grpcService;

        const { item } = await itemsClient.getPublished({
          ...parameters,
        });

        if (!item) {
          return { data: item };
        }

        const carItem = item as CarItemType;

        const extendedCarItems = await getExtendedItems<CarItemType, ExtendedCarItemType>(
          api,
          [carItem],
        );

        const extendedCarItem = extendedCarItems[0];

        // HACK: данный код необходимо переписать на рекурсивное получение объектов ReferenceType
        // Код должен предусматривать два пункта:
        // 1. Свойство типа ReferenceType может быть глубоко-вложенным (например car.options[0].group: ReferenceType)
        // 2. Полученное значение ReferenceType так же может иметь свойства ReferenceType.
        //
        // Задача на доработку: https://tracker.yandex.ru/AUTO-980

        // Расширяем значение options.group значениями ReferenceType. Если они имеются.
        const isExtendOptions = Boolean(extendedCarItem?.data.options?.length);

        if (isExtendOptions) {
          const optionsToItems = (
            extendedCarItem.data.options as Array<
              ReferenceType & { group: ReferenceType }
            >
          ).map(
            option =>
              ({
                data: { ...option, group: { ...option.group } },
              }) as unknown as IItem<ICarOption>,
          );

          const extendedOptionItems = await getExtendedItems(api, optionsToItems);

          extendedCarItem.data.options = extendedOptionItems.map(option => option.data);
        }

        const formattedCarItem = (formatter?.(DataTypeEnum.Car, extendedCarItem) ??
          extendedCarItem) as ExtendedCarItemType;

        return {
          data: formattedCarItem,
        };
      },
    }),
  }),
});

const carsReducer = carsApi.reducer;

export default carsReducer;
